/**
 * Liveness capture helpers, split out from the React component so the flow's
 * security-critical essence is testable without a browser.
 *
 * The invariant these functions encode: the client submits only captured media
 * REFS (a file id), never a liveness/face-match score. Scoring is computed
 * server-side by the verification adapter + ML sidecar — the browser must never
 * be trusted to grade its own selfie.
 */
import { submitLiveness, uploadCollectionFile } from './client';
import { eventFor, fileNameFor, type DocumentKind } from './documents';
import type {
  ChallengeReport,
  ClientLivenessSignal,
  CollectionFlowSnapshot,
} from './types';
// Type-only import — erased at compile time, so this does NOT pull the MediaPipe
// runtime (`face-mesh-loader`) into capture.ts's module graph (keeps capture.test
// browserless).
import type { LivenessOutcome } from './ml/liveness-scorer';

/**
 * Version tag for the client liveness challenge. Bump when the stage sequence or
 * detection thresholds change so downstream analytics can bucket signals by the
 * challenge that produced them.
 *
 * `mp-facemesh-2`: replaced the inline center→blink_twice→turn_left→turn_right
 * ladder with the pure config-driven ChallengeMachine (degree-scale head pose,
 * smile/nod/head_shake support, Schmitt-trigger blink), added the in-browser
 * anti-spoof gate + the numeric per-frame history for server re-validation.
 */
export const CHALLENGE_VERSION = 'mp-facemesh-2';

/**
 * Map the in-browser scorer outcome to the wire-shape advisory signal. Pure.
 * This is NON-AUTHORITATIVE telemetry (see {@link ClientLivenessSignal}); the
 * server never scores from it. The raw numeric history the server RE-VALIDATES
 * travels separately, top-level, as {@link ChallengeReport} (see
 * {@link submitCapturedSelfie}) — keeping this object purely advisory.
 */
export function toClientLivenessSignal(outcome: LivenessOutcome): ClientLivenessSignal {
  return {
    passed: outcome.passed,
    liveness_score: outcome.livenessScore,
    blink_count: outcome.blinkCount,
    turned_left: outcome.turnedLeft,
    turned_right: outcome.turnedRight,
    frames_observed: outcome.framesObserved,
    frames_with_face: outcome.framesWithFace,
    challenge_version: CHALLENGE_VERSION,
  };
}

/** Read a captured media Blob as raw base64 (no `data:` URL prefix). */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('blob read failed'));
    reader.onload = () => {
      // `FileReader.result` is `string | ArrayBuffer | null` because the type
      // cannot know which read method was called; `readAsDataURL` always
      // yields a string. Narrow rather than coerce: `String(arrayBuffer)` is
      // "[object ArrayBuffer]", which would upload as the applicant's ID photo
      // and fail server-side as an unreadable image rather than here.
      const { result } = reader;
      if (typeof result !== 'string') {
        reject(new Error('blob read did not produce a data URL'));
        return;
      }
      resolve(dataUrlToBase64(result));
    };
    reader.readAsDataURL(blob);
  });
}

/** Strip the `data:<mime>;base64,` prefix from a data URL, yielding raw base64. */
export function dataUrlToBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

/** A crop rectangle in the video's INTRINSIC pixel space. */
export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/**
 * Map a rectangle drawn over an `object-fit: cover` `<video>` — expressed in the
 * video element's own DISPLAYED pixel space (origin = element top-left, CSS px) —
 * into the video's INTRINSIC pixel space, so a canvas crop reproduces exactly
 * what the user saw inside that rectangle. Pure; no DOM access.
 *
 * `cover` scales the frame by `max(boxW/Vw, boxH/Vh)` and centres it, clipping
 * the overflow. We invert that: subtract the centring offset, divide by the
 * scale. Because the box rect and the guide rect are measured in the SAME CSS-px
 * space, devicePixelRatio cancels — only their ratio is mapped into sensor px.
 * The result is clamped to the frame so a guide bleeding past an edge can't
 * produce an out-of-bounds crop.
 */
export function coverCropRect(
  videoW: number,
  videoH: number,
  boxW: number,
  boxH: number,
  guideX: number,
  guideY: number,
  guideW: number,
  guideH: number,
): CropRect {
  if (videoW <= 0 || videoH <= 0 || boxW <= 0 || boxH <= 0) {
    return { sx: 0, sy: 0, sw: Math.max(1, videoW), sh: Math.max(1, videoH) };
  }
  const scale = Math.max(boxW / videoW, boxH / videoH);
  const offsetX = (boxW - videoW * scale) / 2;
  const offsetY = (boxH - videoH * scale) / 2;

  let sx = (guideX - offsetX) / scale;
  let sy = (guideY - offsetY) / scale;
  let sw = guideW / scale;
  let sh = guideH / scale;

  // Clamp into the frame (the guide can extend past an edge on wide crops).
  sx = Math.max(0, Math.min(sx, videoW));
  sy = Math.max(0, Math.min(sy, videoH));
  sw = Math.max(1, Math.min(sw, videoW - sx));
  sh = Math.max(1, Math.min(sh, videoH - sy));
  return { sx, sy, sw, sh };
}

/**
 * Draw the current video frame onto a canvas and return JPEG bytes as base64
 * (no data-URL prefix). Pure given a painted `<video>` + `<canvas>`; the caller
 * owns the elements. Throws if a 2D context can't be acquired.
 *
 * With `crop` (intrinsic px), only that sub-region is captured — used by the
 * document flow to save exactly what sat inside the on-screen card guide
 * (matching the `object-fit: cover` preview). Without it, the full frame is
 * captured (the liveness selfie path).
 */
export function grabFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  crop?: CropRect,
): string {
  const w = video.videoWidth || 640;
  const h = video.videoHeight || 480;
  const region: CropRect = crop ?? { sx: 0, sy: 0, sw: w, sh: h };
  canvas.width = Math.max(1, Math.round(region.sw));
  canvas.height = Math.max(1, Math.round(region.sh));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(
    video,
    region.sx,
    region.sy,
    region.sw,
    region.sh,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  return dataUrlToBase64(canvas.toDataURL('image/jpeg', 0.92));
}

/** Optional PAD video + advisory signal + numeric challenge report for a selfie submit. */
export interface LivenessSubmitExtras {
  /** Base64 (no data-URL prefix) of the recorded challenge video (PAD evidence). */
  videoBase64?: string | null;
  /** Base64 of the short clip recorded during the screen-flash step. */
  flashVideoBase64?: string | null;
  /** Advisory, non-authoritative client outcome. Stored untrusted server-side. */
  clientLiveness?: ClientLivenessSignal;
  /**
   * Numeric per-frame history + issued challenges. Rides TOP-LEVEL as
   * `challenge_report` so the server re-derives its own verdict from the numbers
   * (never the client's booleans). See {@link ChallengeReport}.
   */
  challengeReport?: ChallengeReport;
}

/**
 * Upload the captured selfie (+ optional PAD video) and advance the flow past
 * `awaiting_liveness`.
 *
 * The AUTHORITATIVE payload is still only opaque file REFS — the server scores
 * liveness itself and never trusts the browser's grade. When `extras` is
 * omitted the body is byte-identical to before: `{ photo_file_id }`.
 *
 * When the caller passes `extras.clientLiveness`, it rides along under the
 * `client_liveness` key: explicitly NON-AUTHORITATIVE telemetry the server
 * stores untrusted (never gates the decision). A `videoBase64` is uploaded as a
 * second media ref (`video_file_id`) for passive-liveness evidence.
 */
export async function submitCapturedSelfie(
  token: string,
  flowId: string,
  base64: string,
  extras?: LivenessSubmitExtras,
): Promise<CollectionFlowSnapshot> {
  const { file_id } = await uploadCollectionFile(token, {
    content_base64: base64,
    content_type: 'image/jpeg',
    file_name: 'selfie.jpg',
  });

  let videoFileId: string | undefined;
  if (extras?.videoBase64) {
    const uploaded = await uploadCollectionFile(token, {
      content_base64: extras.videoBase64,
      content_type: 'video/webm',
      file_name: 'liveness.webm',
    });
    videoFileId = uploaded.file_id;
  }

  let flashFileId: string | undefined;
  if (extras?.flashVideoBase64) {
    const uploaded = await uploadCollectionFile(token, {
      content_base64: extras.flashVideoBase64,
      content_type: 'video/webm',
      file_name: 'liveness-flash.webm',
    });
    flashFileId = uploaded.file_id;
  }

  return submitLiveness(token, flowId, {
    photo_file_id: file_id,
    ...(videoFileId ? { video_file_id: videoFileId } : {}),
    ...(flashFileId ? { flash_video_file_id: flashFileId } : {}),
    ...(extras?.clientLiveness ? { client_liveness: extras.clientLiveness } : {}),
    ...(extras?.challengeReport ? { challenge_report: extras.challengeReport } : {}),
  });
}

export type DocumentSide = 'front' | 'back';

/**
 * Upload an identity document photo and advance via the event for that document
 * and side (./documents). Payload carries the opaque file ref so the backend can
 * attach it to context without trusting scores, plus the issuing country the
 * applicant chose, when they chose one.
 */
export async function submitCapturedDocument(
  token: string,
  base64: string,
  document: { kind: DocumentKind; side: DocumentSide; country?: string },
  postEvent: (
    token: string,
    type: string,
    payload?: unknown,
  ) => Promise<CollectionFlowSnapshot>,
  /** Silent capture's front-camera frame (./silentCapture), when one was taken. */
  frontFrame?: string | null,
): Promise<CollectionFlowSnapshot> {
  const { kind, side, country } = document;
  const { file_id } = await uploadCollectionFile(token, {
    content_base64: base64,
    content_type: 'image/jpeg',
    file_name: fileNameFor(kind, side),
  });
  // Not named `id-…`: the operator's ID image list matches that prefix, and
  // this is a face, not a document. A failed upload drops the frame only.
  const frame = frontFrame
    ? await uploadCollectionFile(token, {
        content_base64: frontFrame,
        content_type: 'image/jpeg',
        file_name: `document-selfie-${side}.jpg`,
      }).catch(() => null)
    : null;
  return postEvent(token, eventFor(kind, side), {
    file_id,
    side,
    ...(country ? { country } : {}),
    ...(frame ? { front_camera_file_id: frame.file_id } : {}),
  });
}
